import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Icon } from '../components/Layout'
import { getProfile } from '../services/profiles'
import { SocialTasksPanel } from '../components/SocialTasks'
import { BountyPanel } from '../components/BountyPanel'
import CisCptExplainer from '../components/CisCptExplainer'
import { getLevelForCis, formatMultiplier } from '../utils/cisLevels'

const CPX_APP_ID = import.meta.env.VITE_CPX_APP_ID ?? ''

function getCpxUrl(userId) {
  if (!CPX_APP_ID || !userId) return null
  return `https://offers.cpx-research.com/index.php?app_id=${CPX_APP_ID}&ext_user_id=${encodeURIComponent(userId)}&output=desktop-offerwall`
}

function OfferwallFrame({ url, name, envVar }) {
  const [loaded, setLoaded] = useState(false)

  if (!url) {
    return (
      <div className="flex flex-col items-center justify-center h-[500px] border-2 border-dashed border-on-background/20 gap-3">
        <Icon name="wifi_off" className="text-[40px] opacity-20" />
        <p className="font-bold text-xs uppercase tracking-widest opacity-40">
          {name} not configured
        </p>
        <p className="text-[10px] opacity-30">Set {envVar} in environment</p>
      </div>
    )
  }

  return (
    <div className="relative w-full" style={{ height: 600 }}>
      {!loaded && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-surface-container">
          <div className="w-8 h-8 border-4 border-primary-container border-t-transparent animate-spin" />
          <p className="font-bold text-xs uppercase tracking-widest opacity-50">Loading {name}…</p>
        </div>
      )}
      <iframe
        src={url}
        title={name}
        className="w-full h-full border-0"
        onLoad={() => setLoaded(true)}
        allow="autoplay; payment"
        sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-top-navigation"
      />
    </div>
  )
}

function CptBalanceCard({ balance, loading, transactions, cisScore }) {
  const level = getLevelForCis(cisScore)
  return (
    <div className="bg-on-background text-inverse-on-surface border-4 border-on-background neo-shadow p-5 lg:p-8 relative overflow-hidden">
      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="font-bold uppercase text-[10px] tracking-widest opacity-60 mb-1">Your CPT Balance</p>
            {loading ? (
              <div className="h-10 w-36 bg-white/10 animate-pulse" />
            ) : (
              <h2 className="font-extrabold text-[40px] leading-none">
                {Number(balance ?? 0).toLocaleString()}
                <span className="text-[16px] font-bold ml-2 opacity-60">CPT</span>
              </h2>
            )}
          </div>
          <div className="bg-primary-container text-on-primary-fixed px-3 py-1 font-bold text-[10px] uppercase tracking-widest border-2 border-primary-container">
            Internal Balance
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-white/10 pt-3">
          <p className="flex items-center gap-1.5 text-[11px] opacity-50 font-bold uppercase tracking-widest">
            <Icon name="schedule" className="text-[14px]" />
            CPT not claimable until token distribution launches
          </p>
          <Link
            to="/levels"
            className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest whitespace-nowrap opacity-90 hover:opacity-100 hover:underline"
          >
            {level.label} · {formatMultiplier(level.multiplier)} rewards
          </Link>
        </div>
      </div>
      {transactions?.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-4">
          <p className="font-bold uppercase text-[9px] tracking-widest opacity-50 mb-2">Recent Earnings</p>
          <div className="flex flex-col gap-1">
            {transactions.slice(0, 3).map((tx, i) => (
              <div key={i} className="flex justify-between text-[11px]">
                <span className="opacity-60 truncate max-w-[70%]">{tx.description ?? tx.source ?? 'Offer completed'}</span>
                <span className="font-bold text-primary-container">+{tx.amount} CPT</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-white/5 rotate-45 pointer-events-none" />
    </div>
  )
}

const EARN_NETWORKS = [
  {
    id: 'cpx',
    name: 'CPX Research',
    description: 'Take paid surveys matched to your profile and earn CPT.',
    getUrl: getCpxUrl,
    envVar: 'VITE_CPX_APP_ID',
    headerBg: 'bg-on-background',
    headerText: 'text-inverse-on-surface',
    icon: 'assignment',
    status: 'live',
  },
  {
    id: 'adgem',
    name: 'AdGem',
    description: 'More earning opportunities coming soon.',
    getUrl: () => null,
    envVar: '',
    headerBg: 'bg-surface-container',
    headerText: 'text-on-surface-variant',
    icon: 'target',
    status: 'soon',
  },
  {
    id: 'net3',
    name: 'Network 3',
    description: 'More earning opportunities coming soon.',
    getUrl: () => null,
    envVar: '',
    headerBg: 'bg-surface-container',
    headerText: 'text-on-surface-variant',
    icon: 'more_horiz',
    status: 'soon',
  },
  {
    id: 'net4',
    name: 'Network 4',
    description: 'More earning opportunities coming soon.',
    getUrl: () => null,
    envVar: '',
    headerBg: 'bg-surface-container',
    headerText: 'text-on-surface-variant',
    icon: 'more_horiz',
    status: 'soon',
  },
]

function EarnNetworks({ userId }) {
  const [activeNetwork, setActiveNetwork] = useState('cpx')
  const network = EARN_NETWORKS.find(n => n.id === activeNetwork)

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {EARN_NETWORKS.map(n => (
          <button
            key={n.id}
            onClick={() => n.status === 'live' && setActiveNetwork(n.id)}
            disabled={n.status === 'soon'}
            className={`relative p-4 border-2 text-left transition-all ${
              activeNetwork === n.id
                ? 'border-on-background neo-shadow bg-surface-container'
                : n.status === 'soon'
                  ? 'border-on-background/10 opacity-40 cursor-not-allowed bg-surface-container'
                  : 'border-on-background/20 hover:border-on-background/60 bg-surface-container'
            }`}
          >
            {n.status === 'soon' && (
              <span className="absolute top-2 right-2 text-[8px] font-bold uppercase tracking-widest bg-on-background/10 px-1.5 py-0.5">
                Soon
              </span>
            )}
            {activeNetwork === n.id && n.status === 'live' && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-primary-container rounded-full" />
            )}
            <Icon name={n.icon} className="text-[28px] block mb-2 opacity-80" />
            <p className="font-extrabold text-sm uppercase tracking-wide leading-tight">{n.name}</p>
            <p className="text-[10px] text-on-surface-variant mt-1 leading-snug">{n.description}</p>
          </button>
        ))}
      </div>

      <div className={`${network.headerBg} ${network.headerText} p-4 flex items-center justify-between border-2 border-on-background`}>
        <div className="flex items-center gap-3">
          <Icon name={network.icon} className="text-[22px] opacity-80" />
          <div>
            <p className="font-extrabold text-lg uppercase tracking-wide">{network.name}</p>
            <p className="text-[11px] opacity-70">{network.description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="font-bold text-[10px] uppercase tracking-widest opacity-80">Live</span>
        </div>
      </div>

      <div className="border-2 border-on-background/20 overflow-hidden">
        <OfferwallFrame url={network.getUrl(userId)} name={network.name} envVar={network.envVar} />
      </div>
    </div>
  )
}

// ─── Page ───────────────────────────────────────────────────────
export default function Offerwall() {
  const { user } = useAuth()

  const [cptBalance, setCptBalance] = useState(0)
  const [cisScore, setCisScore] = useState(0)
  const [walletLoading, setWalletLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('offers')

  useEffect(() => {
    // CPT is stored in the profile at userStat.cisScore.totalCptEarned.
    // There is no separate /wallet endpoint.
    const fetchCpt = () => {
      getProfile()
        .then(profile => {
          setCptBalance(profile?.cptEarned ?? 0)
          setCisScore(profile?.cisScore ?? 0)
        })
        .catch(() => {})
        .finally(() => setWalletLoading(false))
    }

    fetchCpt()
    const interval = setInterval(fetchCpt, 30_000)
    return () => clearInterval(interval)
  }, [])

  return (
    <div className="flex-1 lg:ml-[300px] w-full max-w-4xl flex flex-col gap-3 lg:gap-5 px-4 lg:px-0 pt-4 lg:pt-0 pb-24 md:pb-8">

      {/* Header */}
      <section className="border border-on-background/10 lg:neo-border lg:neo-shadow p-4 lg:p-8 bg-surface-container">
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-primary-container mb-1">Earn Rewards</p>
        <h1 className="text-[1.6rem] lg:text-display-lg font-extrabold uppercase tracking-tight leading-none mb-2">Offerwall</h1>
        <p className="text-sm text-on-surface-variant max-w-xl">
          Complete surveys and offers to earn rewards. Powered by CPX Research.
        </p>
      </section>

      {/* CPT balance */}
      <CptBalanceCard
        balance={cptBalance}
        loading={walletLoading}
        transactions={[]}
        cisScore={cisScore}
      />

      {/* CIS → CPT explainer — rewards on this page scale with CIS level */}
      <CisCptExplainer />

      {/* Offers vs Social Tasks */}
      <div className="flex gap-2 border-b-2 border-on-background/10">
        <button
          onClick={() => setActiveTab('offers')}
          className={`px-4 py-2.5 font-extrabold text-xs uppercase tracking-widest border-b-2 -mb-0.5 transition-colors ${
            activeTab === 'offers'
              ? 'border-on-background text-on-background'
              : 'border-transparent text-on-surface-variant hover:text-on-background'
          }`}
        >
          Offers
        </button>
        <button
          onClick={() => setActiveTab('social')}
          className={`px-4 py-2.5 font-extrabold text-xs uppercase tracking-widest border-b-2 -mb-0.5 transition-colors ${
            activeTab === 'social'
              ? 'border-on-background text-on-background'
              : 'border-transparent text-on-surface-variant hover:text-on-background'
          }`}
        >
          Social Tasks
        </button>
        <button
          onClick={() => setActiveTab('bounty')}
          className={`px-4 py-2.5 font-extrabold text-xs uppercase tracking-widest border-b-2 -mb-0.5 transition-colors ${
            activeTab === 'bounty'
              ? 'border-on-background text-on-background'
              : 'border-transparent text-on-surface-variant hover:text-on-background'
          }`}
        >
          Bounty
        </button>
      </div>

      {activeTab === 'offers' ? (
        <EarnNetworks userId={user?.profileId ?? user?.id} />
      ) : activeTab === 'social' ? (
        <SocialTasksPanel />
      ) : (
        <BountyPanel />
      )}
    </div>
  )
}
